import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import styles from './index.module.scss';
import { formatBoldTextHTML } from '../../../../utils/formatBoldText';

// Компонент модального окна для изображения
const ImageModal = ({ isOpen, onClose, imageSrc, imageAlt }) => {
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleKeyDown = React.useCallback(
    (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    },
    [onClose],
  );

  React.useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  return createPortal(
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.85)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 99999,
        backdropFilter: 'blur(8px)',
        animation: 'fadeIn 0.3s ease-out',
      }}
      onClick={handleBackdropClick}>
      <div
        style={{
          position: 'relative',
          width: '90vw',
          height: '90vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          animation: 'slideIn 0.3s ease-out',
        }}>
        <button
          style={{
            position: 'absolute',
            top: '20px',
            right: '20px',
            background: 'rgba(255, 255, 255, 0.9)',
            border: 'none',
            borderRadius: '50%',
            width: '50px',
            height: '50px',
            fontSize: '28px',
            fontWeight: 'bold',
            color: '#333',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.3s ease',
            zIndex: 100000,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          }}
          onClick={onClose}
          onMouseEnter={(e) => {
            e.target.style.background = 'rgba(255, 255, 255, 1)';
            e.target.style.transform = 'scale(1.1)';
          }}
          onMouseLeave={(e) => {
            e.target.style.background = 'rgba(255, 255, 255, 0.9)';
            e.target.style.transform = 'scale(1)';
          }}>
          ×
        </button>

        <div
          style={{
            width: '100%',
            height: '80%',
            overflow: 'hidden',
            background: 'transparent',
          }}>
          <div
            style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
            <img
              src={imageSrc}
              alt={imageAlt}
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain',
              }}
            />
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

// Компонент модального окна для слайдера
const SliderModal = ({ isOpen, onClose, images, path_to_folder, initialIndex = 0 }) => {
  const [currentSlide, setCurrentSlide] = useState(initialIndex);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [mouseStart, setMouseStart] = useState(null);
  const [mouseEnd, setMouseEnd] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  const nextSlide = React.useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % images.length);
  }, [images.length]);

  const prevSlide = React.useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + images.length) % images.length);
  }, [images.length]);

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  // Минимальное расстояние для свайпа
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      nextSlide();
    } else if (isRightSwipe) {
      prevSlide();
    }
  };

  // Mouse drag для десктопа в модалке
  const onMouseDown = (e) => {
    // Игнорируем клики по кнопкам
    if (e.target.tagName === 'BUTTON') return;
    setIsDragging(true);
    setMouseEnd(null);
    setMouseStart(e.clientX);
  };

  const onMouseMove = (e) => {
    if (!isDragging) return;
    setMouseEnd(e.clientX);
  };

  const onMouseUp = () => {
    if (!isDragging) return;
    setIsDragging(false);

    if (!mouseStart || !mouseEnd) return;

    const distance = mouseStart - mouseEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      nextSlide();
    } else if (isRightSwipe) {
      prevSlide();
    }
  };

  const onMouseLeave = () => {
    if (isDragging) {
      setIsDragging(false);
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleKeyDown = React.useCallback(
    (e) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowLeft') {
        prevSlide();
      } else if (e.key === 'ArrowRight') {
        nextSlide();
      }
    },
    [onClose, prevSlide, nextSlide],
  );

  React.useEffect(() => {
    if (!isOpen) {
      document.body.style.overflow = 'unset';
      return;
    }

    setCurrentSlide(initialIndex);
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, initialIndex, handleKeyDown]);

  if (!isOpen) return null;

  const isMobile = window.innerWidth <= 768;
  const buttonSize = isMobile ? '40px' : '50px';
  const buttonFontSize = isMobile ? '20px' : '24px';
  const closeFontSize = isMobile ? '24px' : '28px';

  return createPortal(
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.85)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 99999,
        backdropFilter: 'blur(8px)',
        animation: 'fadeIn 0.3s ease-out',
      }}
      onClick={handleBackdropClick}>
      <div
        style={{
          position: 'relative',
          width: '90vw',
          height: '90vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          animation: 'slideIn 0.3s ease-out',
        }}>
        <button
          style={{
            position: 'absolute',
            top: '10px',
            right: '10px',
            background: 'rgba(255, 255, 255, 0.9)',
            border: 'none',
            width: buttonSize,
            height: buttonSize,
            borderRadius: '50%',
            fontSize: closeFontSize,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.3s ease',
            zIndex: 100000,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          }}
          onClick={onClose}
          onMouseEnter={(e) => {
            e.target.style.background = 'white';
            e.target.style.transform = 'scale(1.1)';
          }}
          onMouseLeave={(e) => {
            e.target.style.background = 'rgba(255, 255, 255, 0.9)';
            e.target.style.transform = 'scale(1)';
          }}>
          ×
        </button>

        <button
          style={{
            position: 'absolute',
            top: '50%',
            left: '10px',
            transform: 'translateY(-50%)',
            background: 'rgba(255, 255, 255, 0.9)',
            color: '#333',
            border: 'none',
            width: buttonSize,
            height: buttonSize,
            borderRadius: '50%',
            fontSize: buttonFontSize,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.3s ease',
            zIndex: 100000,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          }}
          onClick={prevSlide}
          onMouseEnter={(e) => {
            e.target.style.background = 'white';
            e.target.style.transform = 'translateY(-50%) scale(1.1)';
          }}
          onMouseLeave={(e) => {
            e.target.style.background = 'rgba(255, 255, 255, 0.9)';
            e.target.style.transform = 'translateY(-50%) scale(1)';
          }}>
          &#8249;
        </button>

        <button
          style={{
            position: 'absolute',
            top: '50%',
            right: '10px',
            transform: 'translateY(-50%)',
            background: 'rgba(255, 255, 255, 0.9)',
            color: '#333',
            border: 'none',
            width: buttonSize,
            height: buttonSize,
            borderRadius: '50%',
            fontSize: buttonFontSize,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.3s ease',
            zIndex: 100000,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          }}
          onClick={nextSlide}
          onMouseEnter={(e) => {
            e.target.style.background = 'white';
            e.target.style.transform = 'translateY(-50%) scale(1.1)';
          }}
          onMouseLeave={(e) => {
            e.target.style.background = 'rgba(255, 255, 255, 0.9)';
            e.target.style.transform = 'translateY(-50%) scale(1)';
          }}>
          &#8250;
        </button>

        <div
          style={{
            width: '100%',
            height: '80%',
            overflow: 'hidden',
            background: 'transparent',
            cursor: isDragging ? 'grabbing' : 'grab',
          }}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={onMouseUp}
          onMouseLeave={onMouseLeave}>
          <div
            style={{
              display: 'flex',
              transition: 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
              width: '100%',
              height: '100%',
              transform: `translateX(-${currentSlide * 100}%)`,
            }}>
            {images.map((imgName, index) => (
              <div
                key={index}
                style={{
                  width: '100%',
                  flexShrink: 0,
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                <img
                  src={`${path_to_folder}${imgName}`}
                  alt={`Slide ${index + 1}`}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain',
                    userSelect: 'none',
                  }}
                  onDragStart={(e) => e.preventDefault()}
                />
              </div>
            ))}
          </div>
        </div>

        <div
          style={{
            position: 'absolute',
            bottom: '20px',
            left: '50%',
            transform: 'translateX(-50%)',
            color: 'white',
            fontSize: '18px',
            background: 'rgba(0, 0, 0, 0.7)',
            padding: '10px 20px',
            borderRadius: '25px',
            backdropFilter: 'blur(10px)',
          }}>
          {currentSlide + 1} / {images.length}
        </div>
      </div>
    </div>,
    document.body,
  );
};

const ImageSlider = ({ images, path_to_folder }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [mouseStart, setMouseStart] = useState(null);
  const [mouseEnd, setMouseEnd] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const slidesWrapperRef = React.useRef(null);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % images.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + images.length) % images.length);
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const openModal = (index) => {
    setCurrentSlide(index);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  // Минимальное расстояние для свайпа
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      nextSlide();
    } else if (isRightSwipe) {
      prevSlide();
    }
  };

  // Mouse drag для десктопа
  const onMouseDown = (e) => {
    setIsDragging(true);
    setMouseEnd(null);
    setMouseStart(e.clientX);
  };

  const onMouseMove = (e) => {
    if (!isDragging) return;
    setMouseEnd(e.clientX);
  };

  const onMouseUp = () => {
    if (!isDragging) return;
    setIsDragging(false);

    if (!mouseStart || !mouseEnd) return;

    const distance = mouseStart - mouseEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      nextSlide();
    } else if (isRightSwipe) {
      prevSlide();
    }
  };

  const onMouseLeave = () => {
    if (isDragging) {
      setIsDragging(false);
    }
  };

  // Обновляем позицию слайдов при изменении размера окна
  React.useEffect(() => {
    const updateSlidePosition = () => {
      if (slidesWrapperRef.current) {
        slidesWrapperRef.current.style.transition = 'none';
        slidesWrapperRef.current.style.transform = `translateX(-${currentSlide * 100}%)`;
        // Возвращаем transition обратно после небольшой задержки
        setTimeout(() => {
          if (slidesWrapperRef.current) {
            slidesWrapperRef.current.style.transition =
              'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)';
          }
        }, 50);
      }
    };

    updateSlidePosition();
    window.addEventListener('resize', updateSlidePosition);
    return () => {
      window.removeEventListener('resize', updateSlidePosition);
    };
  }, [currentSlide]);

  return (
    <>
      <div className={styles.sliderContainer}>
        <div
          className={styles.slider}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={onMouseUp}
          onMouseLeave={onMouseLeave}
          style={{ cursor: isDragging ? 'grabbing' : 'grab' }}>
          <div
            ref={slidesWrapperRef}
            className={styles.slidesWrapper}
            style={{ transform: `translateX(-${currentSlide * 100}%)` }}>
            {images.map((imgName, index) => (
              <div key={index} className={styles.slideContainer}>
                <img
                  src={`${path_to_folder}${imgName}`}
                  alt={`Slide ${index + 1}`}
                  className={styles.slideImage}
                  onClick={() => openModal(index)}
                  style={{ cursor: isDragging ? 'grabbing' : 'pointer', userSelect: 'none' }}
                  onDragStart={(e) => e.preventDefault()}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Кнопки навигации */}
        <button className={styles.prevButton} onClick={prevSlide}>
          &#8249;
        </button>
        <button className={styles.nextButton} onClick={nextSlide}>
          &#8250;
        </button>

        {/* Индикаторы */}
        <div className={styles.indicators}>
          {images.map((_, index) => (
            <button
              key={index}
              className={`${styles.indicator} ${index === currentSlide ? styles.active : ''}`}
              onClick={() => goToSlide(index)}
            />
          ))}
        </div>
      </div>

      {/* Модальное окно слайдера */}
      <SliderModal
        isOpen={isModalOpen}
        onClose={closeModal}
        images={images}
        path_to_folder={path_to_folder}
        initialIndex={currentSlide}
      />
    </>
  );
};

const PublicationContentBlockV2 = ({ blockData, path_to_folder }) => {
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [modalImageSrc, setModalImageSrc] = useState('');
  const [modalImageAlt, setModalImageAlt] = useState('');
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  // Отслеживаем изменение размера окна
  React.useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const handleImageClick = (imageSrc, imageAlt) => {
    // Отключаем модальное окно на мобильных и планшетных устройствах
    if (window.innerWidth <= 1024) {
      return;
    }
    setModalImageSrc(imageSrc);
    setModalImageAlt(imageAlt);
    setIsImageModalOpen(true);
  };

  const handleCloseImageModal = () => {
    setIsImageModalOpen(false);
  };

  const renderContent = (el, index, allContent) => {
    if (
      ((el.cotent_tag === 'description_1' || el.content_tag === 'description_1') || (el.cotent_tag === 'description_2' || el.content_tag === 'description_2')) &&
      el.content_text !== 'none'
    ) {
      const formattedText = formatBoldTextHTML(el.content_text);
      return (
        <div className={styles.description} dangerouslySetInnerHTML={{ __html: formattedText }} />
      );
    } else if ((el.cotent_tag === 'images' || el.content_tag === 'images') && el.imgs_names !== 'none') {
      // Обрабатываем строку imgs_names, разделяем по запятым и очищаем пути
      const imgNames = el.imgs_names
        .split(',')
        .map((name) => {
          // Убираем лишние символы и очищаем путь
          let cleanName = name.trim();
          // Убираем кавычки и скобки
          cleanName = cleanName.replace(/['"[\]]/g, '');
          // Очищаем путь от "/" и "\"
          cleanName = cleanName.split('/').pop().split('\\').pop();
          return cleanName;
        })
        .filter((name) => name && name !== 'none');

      // Проверяем, есть ли подпись после этого изображения/слайдера
      const nextElement = allContent[index + 1];
      const hasSignature =
        nextElement &&
        (nextElement.cotent_tag === 'signature' || nextElement.content_tag === 'signature') &&
        nextElement.content_text !== 'none';

      if (imgNames.length === 1) {
        // Одна картинка
        const imagePath = `${path_to_folder}${imgNames[0]}`;
        return (
          <div className={styles.mediaContainer}>
            <div className={styles.imageContainer}>
              <img
                src={imagePath}
                alt='Контент'
                className={styles.singleImage}
                onClick={() => handleImageClick(imagePath, 'Изображение')}
                style={{ cursor: 'pointer' }}
              />
            </div>
            {hasSignature && (
              <div
                className={styles.signature}
                dangerouslySetInnerHTML={{
                  __html: formatBoldTextHTML(nextElement.content_text),
                }}
              />
            )}
          </div>
        );
      } else if (imgNames.length > 1) {
        // Слайдер
        return (
          <div className={styles.mediaContainer}>
            <ImageSlider key={windowWidth} images={imgNames} path_to_folder={path_to_folder} />
            {hasSignature && (
              <div
                className={styles.signature}
                dangerouslySetInnerHTML={{
                  __html: formatBoldTextHTML(nextElement.content_text),
                }}
              />
            )}
          </div>
        );
      }
    } else if (el.cotent_tag === 'signature' || el.content_tag === 'signature') {
      // Подпись уже обработана в предыдущем элементе, пропускаем
      return null;
    }
    return null;
  };

  if (!blockData || !blockData.block_content || !Array.isArray(blockData.block_content)) {
    return null;
  }

  // Проверяем, есть ли контент для рендеринга
  const hasContent = blockData.block_content.some((el) => {
    if (
      ((el.cotent_tag === 'description_1' || el.content_tag === 'description_1') || (el.cotent_tag === 'description_2' || el.content_tag === 'description_2')) &&
      el.content_text !== 'none'
    ) {
      return true;
    }
    if ((el.cotent_tag === 'images' || el.content_tag === 'images') && el.imgs_names !== 'none') {
      return true;
    }
    return false;
  });

  // Если нет контента, не рендерим блок
  if (!hasContent) {
    return null;
  }

  return (
    <>
      <div className={styles.publicationContentBlockV2}>
        <div className={styles.contentContainer}>
          {blockData.block_content.map((el, index) => (
            <div key={index} className={styles.contentItem}>
              {renderContent(el, index, blockData.block_content)}
            </div>
          ))}
        </div>
      </div>

      <ImageModal
        isOpen={isImageModalOpen}
        onClose={handleCloseImageModal}
        imageSrc={modalImageSrc}
        imageAlt={modalImageAlt}
      />
    </>
  );
};

export default PublicationContentBlockV2;
